import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faExpand } from "@fortawesome/pro-solid-svg-icons/faExpand";
import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons/faCircleQuestion";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  cn,
} from "@wondermaps/ui";
import type { FAQConfig, FAQItem } from "@wondermaps/db/schema";
import { VisitorDrawer } from "./VisitorDrawer";
import { SheetHeader } from "./SheetHeader";
import {
  getTranslatedValue,
  type NameTranslations,
} from "@wondermaps/db/client";

/** Lazy import RichDescription to avoid loading Lexical renderer until needed */
function useLazyRichDescription(shouldLoad: boolean) {
  const [Component, setComponent] = useState<{
    RichDescription: typeof import("./poi/RichDescription").RichDescription | null;
  }>({ RichDescription: null });

  useEffect(() => {
    if (shouldLoad) {
      import("./poi/RichDescription").then((mod) => {
        setComponent({ RichDescription: mod.RichDescription });
      });
    }
  }, [shouldLoad]);

  return Component;
}

interface FAQBottomSheetProps {
  open: boolean;
  onClose: () => void;
  faqConfig: FAQConfig;
  lang: string;
  onExpand?: () => void;
}

export function FAQBottomSheet({
  open,
  onClose,
  faqConfig,
  lang,
  onExpand,
}: FAQBottomSheetProps) {
  const { t } = useTranslation("map");

  // Track whether sheet has ever opened (defers Lexical import until needed)
  const [everOpened, setEverOpened] = useState(false);
  useEffect(() => {
    if (open && !everOpened) setEverOpened(true);
  }, [open, everOpened]);

  const faqTitle = getTranslatedValue(
    faqConfig.title || t("menu.faq"),
    faqConfig.titleTranslations as NameTranslations,
    lang,
  );

  return (
    <VisitorDrawer
      open={open}
      onOpenChange={(o) => !o && onClose()}
      maxHeightClass="max-h-[85vh]"
      title={faqTitle}
    >
      <SheetHeader
        title={faqTitle}
        icon={<FontAwesomeIcon icon={faCircleQuestion} className="h-5 w-5 text-muted-foreground" />}
        onClose={onClose}
        closeLabel={t("faq.close")}
        action={
          onExpand ? (
            <button
              type="button"
              onClick={onExpand}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={t("faq.expand")}
            >
              <FontAwesomeIcon icon={faExpand} className="h-4 w-4 text-muted-foreground" />
            </button>
          ) : undefined
        }
      />

      <div className="flex-1 overflow-y-auto">
        <FAQBody key={String(open)} faqConfig={faqConfig} lang={lang} shouldLoad={everOpened} />
      </div>
    </VisitorDrawer>
  );
}

/* ── Shared body (used by both sheet and full-screen) ── */

export interface FAQBodyProps {
  faqConfig: FAQConfig;
  lang: string;
  /** Defer Lexical import until true (default: true) */
  shouldLoad?: boolean;
}

export function FAQBody({ faqConfig, lang, shouldLoad = true }: FAQBodyProps) {
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const { RichDescription } = useLazyRichDescription(shouldLoad);

  const uncategorizedItems = useMemo(
    () => faqConfig.items.filter((i) => !i.categoryId).sort((a, b) => a.sort_order - b.sort_order),
    [faqConfig.items],
  );

  const enabledCategories = useMemo(
    () => faqConfig.categories.filter((c) => c.enabled).sort((a, b) => a.sort_order - b.sort_order),
    [faqConfig.categories],
  );

  const renderItem = (item: FAQItem) => {
    const question = getTranslatedValue(
      item.question,
      item.questionTranslations as NameTranslations,
      lang,
    );
    const answer = lang !== "en" && item.answerTranslations?.[lang]
      ? item.answerTranslations[lang]
      : item.answer;
    const isOpen = openItemId === item.id;

    return (
      <Collapsible
        key={item.id}
        open={isOpen}
        onOpenChange={(o) => setOpenItemId(o ? item.id : null)}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full px-4 py-2.5 min-h-[44px] text-left text-sm hover:bg-muted/50 transition-colors"
          >
            <span className="pr-2">{question}</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={cn(
                "h-3 w-3 shrink-0 text-muted-foreground transition-transform",
                isOpen && "rotate-180",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-3">
            {RichDescription && <RichDescription content={answer} fallbackText={null} />}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="pb-safe">
      {uncategorizedItems.map(renderItem)}

      {enabledCategories.map((cat) => {
        const catItems = faqConfig.items
          .filter((i) => i.categoryId === cat.id)
          .sort((a, b) => a.sort_order - b.sort_order);
        if (catItems.length === 0) return null;

        const catTitle = getTranslatedValue(
          cat.title,
          cat.titleTranslations as NameTranslations,
          lang,
        );

        return (
          <div key={cat.id}>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase px-4 pt-3 pb-1">
              {catTitle}
            </h4>
            {catItems.map(renderItem)}
          </div>
        );
      })}
    </div>
  );
}
